import React, { useCallback, useContext, useMemo, useState } from 'react';
import { authExchange } from '@urql/exchange-auth';
import { cacheExchange, Cache } from '@urql/exchange-graphcache';
import { useNavigate } from 'react-router-dom';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import {
  createClient,
  dedupExchange,
  fetchExchange,
  makeOperation,
  Provider as UrqlProvider,
  subscriptionExchange,
} from 'urql';
import { v4 as uuidv4 } from 'uuid';
import UnauthApp from '../components/UnauthApp';
import {
  CreateLeagueInvitationMutation,
  CreateLeagueMutation,
  CreatePickMutation,
  LeagueUpdateSubscription,
  LeagueUpdateSubscriptionVariables,
  PickEmLeagueUpdateSubscription,
  RespondToLeagueInvitationMutation,
  SendChatMessageMutation,
  SetLeagueBuyInMutation,
} from '../generated/hooks';
import {
  CreateLeagueInputType,
  CreateLeagueInvitationInputType,
  CreatePickInputType,
  RespondToLeagueInvitationInputType,
  SendChatMessageInputType,
  SetLeagueBuyInInputType,
} from '../generated/schemas';
import { buildUrl } from '../utils';

// This tab's id, sent on every operation as X-Client-Id (see the urql client's fetchOptions below).
//
// The server skips the acting client from its own leagueUpdate broadcast, because that client already
// patched its cache from the mutation response. That skip is keyed on THIS id rather than the user id
// precisely so it stays scoped to one tab: a user with a second tab (or their phone) open gets no
// mutation response there, so that client must still receive the broadcast or it sits stale forever —
// nothing in the app polls.
//
// sessionStorage, not localStorage: it is per-tab (two tabs get two ids, which is the entire point) and
// survives a reload (so a refresh doesn't orphan an in-flight broadcast against a dead id).
const CLIENT_ID_STORAGE_KEY = 'clientId';

function getClientId() {
  const existing = sessionStorage.getItem(CLIENT_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const clientId = uuidv4();
  sessionStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
  return clientId;
}

// Both league types broadcast over the same `leagueUpdate` field but select different entities, so the
// one updates resolver below sees either shape — every list is optional.
type BroadcastPayload = {
  leagueUpdate?:
    | (Partial<NonNullable<LeagueUpdateSubscription['leagueUpdate']>> &
        Partial<NonNullable<PickEmLeagueUpdateSubscription['leagueUpdate']>>)
    | null;
};

type BroadcastPick = NonNullable<NonNullable<PickEmLeagueUpdateSubscription['leagueUpdate']>['picks']>[number];
type BroadcastChatMessage = NonNullable<NonNullable<LeagueUpdateSubscription['leagueUpdate']>['chatMessages']>[number];
type BroadcastMember = NonNullable<NonNullable<LeagueUpdateSubscription['leagueUpdate']>['members']>[number];
type BroadcastLeagueBuyIn = NonNullable<NonNullable<LeagueUpdateSubscription['leagueUpdate']>['leagueBuyIns']>[number];

// Must match the `limit` the Chat panel's own tail query uses (see LeagueDetail/Chat/index.tsx) — a
// mismatch would silently make this link a no-op, since graphcache keys the list by its exact args.
export const CHAT_MESSAGES_PAGE_SIZE = 50;

// Chat messages are a brand-new top-level list on the League (league.chatMessages), so -- like
// stockMarketLeagueTrades -- they don't auto-merge; new ones must be linked in by hand. Only the
// tail page (the Chat panel's initial `before: null` query) is patched: a client scrolled back into
// older history (before: <cursor>) is a separate cached list and must not have live messages spliced
// into it.
//
// The cached array mirrors the QUERY's order, not the eventual display order: chatMessages(before: null)
// resolves server-side with `order_by("-created")` (newest first) -- same as stockMarketLeagueTrades --
// so a brand-new message (always the newest) is PREPENDED here, exactly like trades. The Chat panel's
// own `.reverse()` for oldest -> newest reading is a local, presentation-only transform of that cached
// array; it doesn't change what order things are stored in. Appending here (as an earlier version of
// this function did) put every new message at the position .reverse() maps to the TOP of the thread --
// backwards.
//
// FOOTGUN: the key here is `{ limit: ... }` with NO `before` field at all -- not `{ before: null, ... }`.
// graphcache's key generation strips null/undefined-valued arguments when normalizing a field's args
// (confirmed via cache.inspectFields: a query sent with `before: null` is stored under the key
// `chatMessages({"limit":50})`, identical to one with `before` omitted entirely). Passing `before: null`
// into cache.resolve/cache.link here would silently never match that key and make this whole function
// a no-op -- exactly what happened during initial development.
const CHAT_TAIL_ARGS = { limit: CHAT_MESSAGES_PAGE_SIZE };

function linkBroadcastChatMessages(messages: readonly BroadcastChatMessage[], cache: Cache, leagueId: string) {
  if (!messages.length) {
    return;
  }
  const leagueKey = { __typename: 'LeagueObjectType', id: leagueId };
  const resolved = cache.resolve(leagueKey, 'chatMessages', CHAT_TAIL_ARGS);
  if (!Array.isArray(resolved)) {
    return; // this client hasn't loaded the chat tail -- nothing to patch
  }
  const messageKeys = resolved as string[];
  const messageKeySet = new Set(messageKeys);
  const newMessageKeys: string[] = [];
  for (const message of messages) {
    if (!message) {
      continue;
    }
    const messageKey = cache.keyOfEntity({ __typename: 'ChatMessageObjectType', id: message.id });
    if (messageKey && !messageKeySet.has(messageKey)) {
      newMessageKeys.push(messageKey);
      messageKeySet.add(messageKey);
    }
  }
  if (newMessageKeys.length > 0) {
    cache.link(leagueKey, 'chatMessages', CHAT_TAIL_ARGS, [...newMessageKeys, ...messageKeys]);
  }
}

// A newly created pick is a brand-new entity: it merges into the cache by id, but belongs to no list
// yet, so ScoresTables (which renders member.picks) would never show it. Link it into its owner's list.
// An EDITED pick keeps its id and auto-merges, so only new ones need this.
//
// member.picks takes a `week` argument, so the cached list is keyed by it — the pick's own game.week is
// that key. The owner is found by matching the pick's user against the cached members list.
//
// Note what is NOT here: any concealment. pickedHomeTeam is already stripped server-side for other
// members until kickoff, so a rival's choice never reaches this cache at all.
function linkBroadcastPicks(picks: readonly BroadcastPick[], cache: Cache, leagueId: string) {
  if (!picks.length) {
    return;
  }
  // PicksWeek.tsx's own query caches this as plain `members` (no orderByScore) -- that's the list whose
  // member entities carry the `picks(week:)` field this function links into. Standings.tsx/Payouts.tsx
  // separately cache `members(orderByScore: true)`, which is a different cache key entirely and was
  // never what's populated by loading the Picks tab; resolving against it here left this whole function
  // a silent no-op whenever the Picks tab was the first thing loaded (see the "Change pick em default tab
  // to current week" commit, which stopped Standings loading first and made this dependency load-bearing).
  const memberKeys = cache.resolve({ __typename: 'LeagueObjectType', id: leagueId }, 'members');
  if (!Array.isArray(memberKeys)) {
    return; // this client hasn't loaded the Pick Em tables — nothing to patch
  }
  for (const pick of picks) {
    if (!pick) {
      continue;
    }
    const ownerUserKey = cache.keyOfEntity({ __typename: 'UserObjectType', id: pick.user.id });
    const ownerKey = (memberKeys as string[]).find(memberKey => cache.resolve(memberKey, 'user') === ownerUserKey);
    if (!ownerKey) {
      continue;
    }
    const week = pick.game.week;
    const resolvedPicks = cache.resolve(ownerKey, 'picks', { week });
    if (!Array.isArray(resolvedPicks)) {
      continue; // that week isn't in cache on this client
    }
    const pickKeys = resolvedPicks as string[];
    const pickKey = cache.keyOfEntity({ __typename: 'PickEmLeagueGamePickObjectType', id: pick.id });
    if (pickKey && !pickKeys.includes(pickKey)) {
      cache.link(ownerKey, 'picks', { week }, [...pickKeys, pickKey]);
    }
  }
}

// A brand-new member (someone who just accepted a league invitation) merges into the cache by id, but
// belongs to no list yet -- unlike an EXISTING member's cash/positions/score, which merge onto its
// entity by id with no linking needed. This is the plain, unordered `members` field (StandingsTable's
// roster on the stock-market side; Pick Em has no equivalent -- its roster is always the ordered
// `standings` relink above, which already covers a new member for free since it re-links the COMPLETE
// list every time). Only patches a list already in cache; a client that hasn't visited the roster page
// loads it fresh on first view.
function linkBroadcastMembers(members: readonly BroadcastMember[], cache: Cache, leagueId: string) {
  if (!members.length) {
    return;
  }
  const leagueKey = { __typename: 'LeagueObjectType', id: leagueId };
  const resolved = cache.resolve(leagueKey, 'members');
  if (!Array.isArray(resolved)) {
    return; // this client hasn't loaded the roster -- nothing to patch
  }
  const memberKeys = resolved as string[];
  const memberKeySet = new Set(memberKeys);
  const newMemberKeys: string[] = [];
  for (const member of members) {
    if (!member) {
      continue;
    }
    const memberKey = cache.keyOfEntity({ __typename: 'LeagueMemberObjectType', id: member.id });
    if (memberKey && !memberKeySet.has(memberKey)) {
      newMemberKeys.push(memberKey);
      memberKeySet.add(memberKey);
    }
  }
  if (newMemberKeys.length > 0) {
    cache.link(leagueKey, 'members', [...memberKeys, ...newMemberKeys]);
  }
}

// A member's `buyIn` is a single sealed entity field (only the paying member and the commissioner ever
// select it), not a list -- so unlike picks/trades/orders above, an EXISTING buyIn auto-merges by id
// with no linking needed. Only the FIRST record of a payment needs this: that LeagueBuyIn row didn't
// exist before, so its id has never been linked to member.buyIn, and per the graphcache FOOTGUN it would
// otherwise merge into the cache and render nowhere. Safe to call unconditionally -- re-linking an
// already-correct link is a no-op.
function linkBroadcastLeagueBuyIns(leagueBuyIns: readonly BroadcastLeagueBuyIn[], cache: Cache) {
  for (const buyIn of leagueBuyIns) {
    if (!buyIn) {
      continue;
    }
    const memberKey = cache.keyOfEntity({ __typename: 'LeagueMemberObjectType', id: buyIn.leagueMember.id });
    const buyInKey = cache.keyOfEntity({ __typename: 'LeagueBuyInObjectType', id: buyIn.id });
    if (memberKey && buyInKey) {
      cache.link(memberKey, 'buyIn', buyInKey);
    }
  }
}

// Normalized cache config. Every entity keys on its own `id` (graphcache's default), so a model
// returned by any mutation or subscription patches every query that holds it — no refetch. Only the
// id-less computed/value types are force-embedded so graphcache doesn't try (and fail) to key them.
//
// A factory over the signed-in userId: the one hand-written cache update (below) needs to know which
// revealed bids are the viewer's own.
const buildGraphcacheConfig = (userId: string) => ({
  keys: {
    TeamStylesObjectType: () => null,
    GameLiveScoreObjectType: () => null,
    MarketOpenWindowType: () => null,
    StockMarketOrderResultObjectType: () => null,
  },
  updates: {
    Subscription: {
      // A member's own preseasonAuctionBids list is sealed, so leagueUpdate never broadcasts it as a
      // field. But when a team processes, an insufficient-funds bid can be SPLIT off as a brand-new
      // entity. That bid arrives (fully populated) inside the public team reveal, but nothing links it
      // into the actor's private member.preseasonAuctionBids — so "My Bids" would miss it until a
      // refetch. Here we do exactly that link, from the public reveal already in cache: for each
      // revealed bid the viewer owns, append it to their member's bid list if it's not already there.
      // (The won/updated halves share their id and auto-merge; only the new split needs linking.)
      leagueUpdate: (result: BroadcastPayload, args: LeagueUpdateSubscriptionVariables, cache: Cache) => {
        const payload = result?.leagueUpdate;
        if (!payload) {
          return;
        }
        const leagueKey = { __typename: 'LeagueObjectType', id: args.leagueId };

        linkBroadcastPicks(payload.picks ?? [], cache, args.leagueId);

        // Standings. Scores merge onto the member entities by id on their own — but members(orderByScore:)
        // is ordered by the SERVER, and merging a field never re-sorts a list. So when a game completes
        // and scores change, the table would show the right numbers in the wrong order. Re-link the list
        // in the order the server just sent. Safe only because `standings` is the COMPLETE member list
        // (that's why it isn't reusing `members`, which carries a subset).
        const standings = payload.standings ?? [];
        if (standings.length > 0) {
          const orderedKeys = standings.flatMap(member => {
            const key = member && cache.keyOfEntity({ __typename: 'LeagueMemberObjectType', id: member.id });
            return key ? [key] : [];
          });
          if (orderedKeys.length === standings.length) {
            cache.link({ __typename: 'LeagueObjectType', id: args.leagueId }, 'members', { orderByScore: true }, orderedKeys);
          }
        }

        // A member's own preseasonAuctionBids list is sealed, so leagueUpdate never broadcasts it as a
        // field. But when a team processes, an insufficient-funds bid can be SPLIT off as a brand-new
        // entity. That bid arrives (fully populated) inside the public team reveal, but nothing links it
        // into the actor's private member.preseasonAuctionBids — so "My Bids" would miss it until a
        // refetch. Here we do exactly that link, from the public reveal already in cache: for each
        // revealed bid the viewer owns, append it to their member's bid list if it's not already there.
        // (The won/updated halves share their id and auto-merge; only the new split needs linking.)
        const memberKey = userId ? cache.resolve(leagueKey, 'member', { userId }) : null;
        if (typeof memberKey === 'string') {
          const resolvedBids = cache.resolve(memberKey, 'preseasonAuctionBids');
          const currentBidKeys = Array.isArray(resolvedBids) ? (resolvedBids as string[]) : [];
          const bidKeySet = new Set(currentBidKeys);
          const nextBidKeys = [...currentBidKeys];
          let bidsChanged = false;
          for (const team of payload.teams || []) {
            for (const bid of team.preseasonAuctionBids || []) {
              if (!bid || bid.user?.id !== userId) {
                continue;
              }
              const bidKey = cache.keyOfEntity({ __typename: 'PreseasonAuctionBidObjectType', id: bid.id });
              if (bidKey && !bidKeySet.has(bidKey)) {
                nextBidKeys.push(bidKey);
                bidKeySet.add(bidKey);
                bidsChanged = true;
              }
            }
          }
          if (bidsChanged) {
            cache.link(memberKey, 'preseasonAuctionBids', nextBidKeys);
          }
        }

        // Trades are a top-level list on the League (TradeHistory reads league.stockMarketLeagueTrades),
        // so unlike entity fields they don't auto-merge — new prints must be prepended by hand. Only
        // patch when the feed is already in cache; otherwise it loads fresh on first view.
        const resolvedTrades = cache.resolve(leagueKey, 'stockMarketLeagueTrades');
        if (Array.isArray(resolvedTrades)) {
          const tradeKeys = resolvedTrades as string[];
          const tradeKeySet = new Set(tradeKeys);
          const newTradeKeys: string[] = [];
          for (const trade of payload.trades || []) {
            if (!trade) {
              continue;
            }
            const tradeKey = cache.keyOfEntity({ __typename: 'StockMarketLeagueTradeObjectType', id: trade.id });
            if (tradeKey && !tradeKeySet.has(tradeKey)) {
              newTradeKeys.push(tradeKey);
              tradeKeySet.add(tradeKey);
            }
          }
          if (newTradeKeys.length > 0) {
            cache.link(leagueKey, 'stockMarketLeagueTrades', [...newTradeKeys, ...tradeKeys]);
          }
        }

        // Order book: a new resting order (from anyone) must appear in the anonymous per-team/per-side
        // book. Fills and cancels arrive as status changes that auto-merge on the order entity by id and
        // drop out via the component's OPEN filter, so only OPEN orders need linking. Only patch a book
        // already in cache (i.e. this team's page is loaded on this client).
        for (const order of payload.orders || []) {
          if (!order || order.status !== 'OPEN') {
            continue;
          }
          const teamKey = cache.keyOfEntity({ __typename: 'TeamObjectType', id: order.team.id });
          const orderKey = cache.keyOfEntity({ __typename: 'StockMarketLeagueOrderObjectType', id: order.id });
          if (!teamKey || !orderKey) {
            continue;
          }
          const resolvedBook = cache.resolve(teamKey, 'stockMarketLeagueOrders', { side: order.side });
          if (!Array.isArray(resolvedBook)) {
            continue; // this client hasn't loaded the team's book — nothing to patch
          }
          const bookKeys = resolvedBook as string[];
          if (!bookKeys.includes(orderKey)) {
            cache.link(teamKey, 'stockMarketLeagueOrders', { side: order.side }, [...bookKeys, orderKey]);
          }
        }

        linkBroadcastMembers(payload.members ?? [], cache, args.leagueId);

        linkBroadcastLeagueBuyIns(payload.leagueBuyIns ?? [], cache);

        linkBroadcastChatMessages(payload.chatMessages ?? [], cache, args.leagueId);

        // No manual chatUnreadCount/chatLastReadAt handling needed here, unlike the picks/trades/orders
        // above: those are viewer-relative fields on the `league` entity itself (LeagueObjectType,
        // gql/league.py), and payload.league -- selected right alongside chatMessages in
        // useLeagueUpdate.ts/usePickEmLeagueUpdate.ts -- carries them fresh on every chat-triggered
        // broadcast. Since League merges by id like any other entity, graphcache applies that update
        // automatically: every OTHER member's badge, and the sender's own other tabs/devices, all just
        // pick up the new values with no cache.invalidate and no refetch.
      },
    },
    Mutation: {
      // The actor is skipped from its own broadcast, so its own new pick arrives here instead — and it
      // needs the same linking, or your very first pick on a game would merge into the cache and then
      // render nowhere.
      // NB: graphcache hands us the FIELD's arguments, not the operation's variables — the field is
      // createPick(input: ...), so it's args.input, regardless of what the variable is named.
      createPick: (result: CreatePickMutation, args: { input: CreatePickInputType }, cache: Cache) => {
        linkBroadcastPicks(result?.createPick?.update?.picks ?? [], cache, args.input.leagueId);
      },
      // Same reason as createPick above: the actor is skipped from its own broadcast, and a member's
      // FIRST-ever payment record is a brand-new LeagueBuyIn entity with nothing to auto-merge onto.
      setLeagueBuyIn: (result: SetLeagueBuyInMutation, _args: { input: SetLeagueBuyInInputType }, cache: Cache) => {
        linkBroadcastLeagueBuyIns(result?.setLeagueBuyIn?.update?.leagueBuyIns ?? [], cache);
      },
      // The actor is skipped from its own broadcast, so the sender's own message arrives here instead,
      // via the mutation's own `update` payload -- same reason createPick needs this. update.league
      // (chatUnreadCount/chatLastReadAt) needs no handling here, same as the subscription handler
      // above: it auto-merges into the League entity by id, no manual cache update required. MarkChatRead
      // doesn't need an entry at all for the same reason -- its own `update.league` (see its mutation
      // document in Chat/index.tsx) merges automatically too.
      sendChatMessage: (result: SendChatMessageMutation, args: { input: SendChatMessageInputType }, cache: Cache) => {
        linkBroadcastChatMessages(result?.sendChatMessage?.update?.chatMessages ?? [], cache, args.input.leagueId);
      },
      // A newly created league is a brand-new entity with no list to auto-merge into: `leagues` is a
      // root Query field, so nothing links it there on its own. The mutation response is populated with
      // every field LeagueList/useLeagueSelectorItems select, so once linked both read straight from
      // cache with no refetch.
      // NB: graphcache hands us the FIELD's arguments — the field is createLeague(input: ...), so it's
      // args.input regardless of the operation variable's name.
      createLeague: (result: CreateLeagueMutation, _args: { input: CreateLeagueInputType }, cache: Cache) => {
        const league = result?.createLeague?.league;
        if (!league) {
          return;
        }
        const leagueKey = cache.keyOfEntity({ __typename: 'LeagueObjectType', id: league.id });
        if (!leagueKey) {
          return;
        }
        const resolvedLeagues = cache.resolve({ __typename: 'Query' }, 'leagues');
        if (!Array.isArray(resolvedLeagues)) {
          return; // nothing cached yet -- it'll load fresh on next visit
        }
        const leagueKeys = resolvedLeagues as string[];
        if (!leagueKeys.includes(leagueKey)) {
          cache.link({ __typename: 'Query' }, 'leagues', [...leagueKeys, leagueKey]);
        }
      },
      // Accepting/declining a league invitation is a root-Query list problem, twice over: the
      // responded invitation must drop out of `invitations` (a pending-only list -- see
      // Query.resolve_invitations -- so it's never coming back), and on accept the now-joined league
      // must appear in `leagues`, neither of which auto-merges just because the entities themselves do.
      // The League entity's own nested `members`/`invitations` lists (LeagueList's roster and pending-
      // invite display) DO need no special handling here, despite being lists too: the mutation
      // response's `league` selection deliberately mirrors fetchUserLeagues' exactly (see the mutation
      // document in LeagueList.tsx), so graphcache overwrites those nested list fields the same way any
      // query result would -- this is not the flat, top-level broadcast-payload shape the subscription
      // handlers above have to manually relink.
      // NB: graphcache hands us the FIELD's arguments — the field is respondToLeagueInvitation(input:
      // ...), so it's args.input regardless of the operation variable's name.
      respondToLeagueInvitation: (
        result: RespondToLeagueInvitationMutation,
        args: { input: RespondToLeagueInvitationInputType },
        cache: Cache,
      ) => {
        if (!result?.respondToLeagueInvitation?.ok) {
          return; // mutation errored server-side (e.g. "Cannot join after the auction has begun") -- leave the invitation in place
        }
        const invitationKey = cache.keyOfEntity({
          __typename: 'LeagueInvitationObjectType',
          id: args.input.invitationId,
        });
        const resolvedInvitations = cache.resolve({ __typename: 'Query' }, 'invitations');
        if (invitationKey && Array.isArray(resolvedInvitations)) {
          const invitationKeys = resolvedInvitations as string[];
          if (invitationKeys.includes(invitationKey)) {
            cache.link(
              { __typename: 'Query' },
              'invitations',
              invitationKeys.filter(key => key !== invitationKey),
            );
          }
        }

        const league = result?.respondToLeagueInvitation?.league;
        if (!args.input.accept || !league) {
          return;
        }
        const leagueKey = cache.keyOfEntity({ __typename: 'LeagueObjectType', id: league.id });
        if (!leagueKey) {
          return;
        }
        const resolvedLeagues = cache.resolve({ __typename: 'Query' }, 'leagues');
        if (!Array.isArray(resolvedLeagues)) {
          return; // nothing cached yet -- it'll load fresh on next visit
        }
        const leagueKeys = resolvedLeagues as string[];
        if (!leagueKeys.includes(leagueKey)) {
          cache.link({ __typename: 'Query' }, 'leagues', [...leagueKeys, leagueKey]);
        }
      },
      // A brand-new invitation is, once again, a list-linking problem: it merges into the cache by id
      // but the commissioner's Manage League page (InvitationsPanel's `league.invitations`) has no way
      // to know it belongs in that list, so it would render nowhere until a refetch. Only patches a
      // list already in cache (i.e. the page is open on this client).
      // NB: graphcache hands us the FIELD's arguments — the field is createLeagueInvitation(input:
      // ...), so it's args.input regardless of the operation variable's name.
      createLeagueInvitation: (
        result: CreateLeagueInvitationMutation,
        args: { input: CreateLeagueInvitationInputType },
        cache: Cache,
      ) => {
        const invitation = result?.createLeagueInvitation?.invitation;
        if (!invitation) {
          return;
        }
        const invitationKey = cache.keyOfEntity({ __typename: 'LeagueInvitationObjectType', id: invitation.id });
        if (!invitationKey) {
          return;
        }
        const leagueKey = { __typename: 'LeagueObjectType', id: args.input.leagueId };
        const resolvedInvitations = cache.resolve(leagueKey, 'invitations');
        if (!Array.isArray(resolvedInvitations)) {
          return; // this client hasn't loaded Manage League's invitations panel -- nothing to patch
        }
        const invitationKeys = resolvedInvitations as string[];
        if (!invitationKeys.includes(invitationKey)) {
          cache.link(leagueKey, 'invitations', [...invitationKeys, invitationKey]);
        }
      },
    },
  },
});

function isTokenValid(token: string) {
  if (!token) {
    return false;
  }
  const decodedToken = JSON.parse(atob(token.split('.')[1]));
  const expDate = new Date(0);
  expDate.setUTCMilliseconds(decodedToken.exp * 1000);
  return expDate > new Date();
}

type AccountContextValue = {
  refreshToken: string;
  userId?: string;
  logout(): void;
  setRefreshToken(token: string): void;
};

const AccountContext = React.createContext<AccountContextValue>({
  refreshToken: null,
  userId: null,
  logout: () => null,
  setRefreshToken: () => null,
});

type AccountProviderProps = {
  children: React.ReactNode;
};

function AccountProvider({ children }: AccountProviderProps) {
  const [refreshToken, setRefreshTokenState] = useState(() => {
    const token = localStorage.getItem('refreshToken');
    return isTokenValid(token) ? token : null;
  });
  const setRefreshToken = useCallback(
    (token: string) => {
      localStorage.setItem('refreshToken', token);
      setRefreshTokenState(token);
    },
    [setRefreshTokenState],
  );
  const navigate = useNavigate();
  const logout = useCallback(() => {
    localStorage.removeItem('refreshToken');
    setRefreshTokenState(null);
    navigate('/');
  }, [setRefreshTokenState]);
  const userId = useMemo(() => {
    if (!refreshToken) {
      return null;
    }
    return JSON.parse(atob(refreshToken.split('.')[1])).user_id as string;
  }, [refreshToken]);
  const urqlClient = useMemo(() => {
    if (!refreshToken) {
      return null;
    }
    const subscriptionClient = new SubscriptionClient(buildUrl('/subscriptions', process.env.REACT_APP_WS_URL), {
      reconnect: true,
    });
    return createClient({
      url: buildUrl('/api/graphql'),
      // Set on the client so EVERY operation carries it. It must be a plain object, not a function:
      // urql copies this into each operation's context, and the subscription transport ships that
      // context to the server as its GQL_START payload — which is how the header reaches the socket
      // too (the same path Authorization already takes). A function wouldn't survive that trip.
      fetchOptions: { headers: { 'X-Client-Id': getClientId() } },
      exchanges: [
        dedupExchange,
        cacheExchange(buildGraphcacheConfig(userId ?? '')),
        authExchange({
          getAuth: async ({ authState }: { authState: { token: string } }) => {
            if (!isTokenValid(authState?.token)) {
              const response = await fetch(buildUrl('/api/token/refresh'), {
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                method: 'POST',
                mode: 'cors',
                body: JSON.stringify({ refresh: refreshToken }),
              });
              if (response.status === 200) {
                const responseData = await response.json();
                return { token: responseData.access };
              }
              return null;
            }
            return null;
          },
          addAuthToOperation: ({ authState, operation }) => {
            if (!isTokenValid(authState?.token)) {
              return operation;
            }
            const fetchOptions =
              typeof operation.context.fetchOptions === 'function'
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {};
            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: `Bearer ${authState.token}`,
                },
              },
            });
          },
          willAuthError: ({ authState }) => !isTokenValid(authState?.token),
          didAuthError: ({ error }) => error.graphQLErrors.some(e => e.message === 'Permission denied'),
        }),
        fetchExchange,
        subscriptionExchange({
          forwardSubscription: operation => {
            return subscriptionClient.request(operation);
          },
        }),
      ],
    });
  }, [refreshToken, userId]);

  return (
    <AccountContext.Provider value={{ refreshToken, userId, logout, setRefreshToken }}>
      {refreshToken ? <UrqlProvider value={urqlClient}>{children}</UrqlProvider> : <UnauthApp />}
    </AccountContext.Provider>
  );
}

export function useAccountContext() {
  return useContext(AccountContext);
}

export default AccountProvider;
